import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader } from '../ui/card';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Button } from '../ui/button';
import { Edit2, Save, X, Plus, Trash2 } from 'lucide-react';
import useRestaurants from '../../hooks/useRestaurants';
import { useSettings } from '../../contexts/SettingsContext';
import { updateRestaurant } from '../../api/restaurantsApi';
import { formatPhoneNumber } from '../../utils/formatters';
import TransferNumbersSection from './TransferNumbersSection';
import LeadsEmailSection from './LeadsEmailSection';

/**
 * OrganizationTab Component
 * 
 * Manages restaurant identity, contact details, and hours of operation.
 * Loads existing restaurant data and allows editing.
 */
const OrganizationTab = () => {
  const { restaurants, isLoading, refetch } = useRestaurants();
  const { selectedLocationId, setSelectedLocationId } = useSettings();
  const [isEditMode, setIsEditMode] = useState(false);
  const [isEditHoursMode, setIsEditHoursMode] = useState(false);
  const [isEditHolidayHoursMode, setIsEditHolidayHoursMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Get selected restaurant to check for outbound feature
  const selectedRestaurant = restaurants?.find(r => r.locationId === selectedLocationId);

  // Auto-select first restaurant if "all" is selected (Organization tab needs a specific restaurant)
  useEffect(() => {
    if (selectedLocationId === 'all' && restaurants && restaurants.length > 0 && !isLoading) {
      setSelectedLocationId(restaurants[0].locationId);
    }
  }, [selectedLocationId, restaurants, isLoading, setSelectedLocationId]);

  // General Info State
  const [restaurantName, setRestaurantName] = useState('');
  const [address, setAddress] = useState('');
  const [timeZone, setTimeZone] = useState('America/New_York');
  const [primaryLanguage, setPrimaryLanguage] = useState('en');
  const [outboundReportRecipient, setOutboundReportRecipient] = useState('');

  // Hours of Operation State
  const [hours, setHours] = useState({
    monday: { open: '09:00', close: '21:00', closed: false },
    tuesday: { open: '09:00', close: '21:00', closed: false },
    wednesday: { open: '09:00', close: '21:00', closed: false },
    thursday: { open: '09:00', close: '21:00', closed: false },
    friday: { open: '09:00', close: '21:00', closed: false },
    saturday: { open: '09:00', close: '21:00', closed: false },
    sunday: { open: '09:00', close: '21:00', closed: false },
  });

  // Holiday Hours State
  const [holidayHours, setHolidayHours] = useState([]);

  // Original values for cancel functionality
  const [originalGeneralInfo, setOriginalGeneralInfo] = useState(null);
  const [originalHours, setOriginalHours] = useState(null);
  const [originalHolidayHours, setOriginalHolidayHours] = useState(null);

  // Parse hours from "11:00-22:00" format to { open, close }
  const parseHours = (hoursString) => {
    if (!hoursString || hoursString === 'Closed' || hoursString === 'closed') {
      return { open: '09:00', close: '21:00', closed: true };
    }
    const [open, close] = hoursString.split('-');
    return {
      open: open?.trim() || '09:00',
      close: close?.trim() || '21:00',
      closed: false
    };
  };

  // Parse holiday hours from backend format
  const parseHolidayHours = (holidayHoursArray) => {
    if (!Array.isArray(holidayHoursArray)) return [];
    
    return holidayHoursArray.map((holiday, index) => {
      const isClosed = holiday.hours === 'CLOSED' || holiday.hours === 'Closed' || holiday.hours === 'closed';
      let open = '09:00';
      let close = '21:00';
      
      if (!isClosed && holiday.hours) {
        const [openTime, closeTime] = holiday.hours.split('-');
        open = openTime?.trim() || '09:00';
        close = closeTime?.trim() || '21:00';
      }
      
      return {
        id: `holiday-${index}-${Date.now()}`,
        name: holiday.name || '',
        date: holiday.date || '',
        open,
        close,
        closed: isClosed
      };
    });
  };

  // Load restaurant data when selection changes
  useEffect(() => {
    if (restaurants && restaurants.length > 0 && selectedLocationId) {
      const restaurant = restaurants.find(r => r.locationId === selectedLocationId);
      if (!restaurant) return;

      // Set form values from restaurant data
      setRestaurantName(restaurant.restaurantName || '');
      
      setAddress(restaurant.address || '');
      setTimeZone(restaurant.timeZone || 'America/New_York');
      setPrimaryLanguage(restaurant.primaryLanguage || 'en');
      setOutboundReportRecipient(restaurant.campaignEmail || '');

      // Parse hours
      if (restaurant.hours && typeof restaurant.hours === 'object') {
        const parsedHours = {
          monday: parseHours(restaurant.hours.Monday || restaurant.hours.monday),
          tuesday: parseHours(restaurant.hours.Tuesday || restaurant.hours.tuesday),
          wednesday: parseHours(restaurant.hours.Wednesday || restaurant.hours.wednesday),
          thursday: parseHours(restaurant.hours.Thursday || restaurant.hours.thursday),
          friday: parseHours(restaurant.hours.Friday || restaurant.hours.friday),
          saturday: parseHours(restaurant.hours.Saturday || restaurant.hours.saturday),
          sunday: parseHours(restaurant.hours.Sunday || restaurant.hours.sunday),
        };
        setHours(parsedHours);
      }

      // Parse holiday hours
      if (restaurant.holidayHours) {
        setHolidayHours(parseHolidayHours(restaurant.holidayHours));
      } else {
        setHolidayHours([]);
      }
    }
  }, [restaurants, selectedLocationId]);

  // Handle edit mode toggle for General Info
  const handleEditGeneralInfo = () => {
    setIsEditMode(true);
    setOriginalGeneralInfo({
      restaurantName,
      address,
      timeZone,
      primaryLanguage,
      outboundReportRecipient
    });
  };

  // Handle edit mode toggle for Hours
  const handleEditHours = () => {
    setIsEditHoursMode(true);
    setOriginalHours(JSON.parse(JSON.stringify(hours)));
  };

  // Handle edit mode toggle for Holiday Hours
  const handleEditHolidayHours = () => {
    setIsEditHolidayHoursMode(true);
    setOriginalHolidayHours(JSON.parse(JSON.stringify(holidayHours)));
  };

  // Handle cancel for General Info
  const handleCancelGeneralInfo = () => {
    if (originalGeneralInfo) {
      setRestaurantName(originalGeneralInfo.restaurantName);
      setAddress(originalGeneralInfo.address);
      setTimeZone(originalGeneralInfo.timeZone);
      setPrimaryLanguage(originalGeneralInfo.primaryLanguage);
      setOutboundReportRecipient(originalGeneralInfo.outboundReportRecipient || '');
    }
    setIsEditMode(false);
  };

  // Handle cancel for Hours
  const handleCancelHours = () => {
    if (originalHours) {
      setHours(JSON.parse(JSON.stringify(originalHours)));
    }
    setIsEditHoursMode(false);
  };

  // Handle cancel for Holiday Hours
  const handleCancelHolidayHours = () => {
    if (originalHolidayHours) {
      setHolidayHours(JSON.parse(JSON.stringify(originalHolidayHours)));
    }
    setIsEditHolidayHoursMode(false);
  };

  // Handle save for General Info
  const handleSaveGeneralInfo = async () => {
    if (!selectedLocationId) {
      alert('Please select a restaurant');
      return;
    }

    setIsSaving(true);
    try {
      const updateData = {
        restaurantName,
        address,
        timeZone,
        primaryLanguage,
        campaignEmail: outboundReportRecipient
      };

      await updateRestaurant(selectedLocationId, updateData);

      setOriginalGeneralInfo({
        restaurantName,
        address,
        timeZone,
        primaryLanguage,
        outboundReportRecipient
      });
      setIsEditMode(false);
      
      // Refresh restaurant data from DynamoDB
      if (refetch) {
        await refetch();
      }
    } catch (error) {
      console.error('Error saving general info:', error);
      alert(error.message || 'Failed to save general info. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // Handle save for Hours
  const handleSaveHours = async () => {
    if (!selectedLocationId) {
      alert('Please select a restaurant');
      return;
    }

    setIsSaving(true);
    try {
      // Convert hours object to DynamoDB format
      const hoursFormat = {
        Monday: hours.monday.closed ? 'Closed' : `${hours.monday.open}-${hours.monday.close}`,
        Tuesday: hours.tuesday.closed ? 'Closed' : `${hours.tuesday.open}-${hours.tuesday.close}`,
        Wednesday: hours.wednesday.closed ? 'Closed' : `${hours.wednesday.open}-${hours.wednesday.close}`,
        Thursday: hours.thursday.closed ? 'Closed' : `${hours.thursday.open}-${hours.thursday.close}`,
        Friday: hours.friday.closed ? 'Closed' : `${hours.friday.open}-${hours.friday.close}`,
        Saturday: hours.saturday.closed ? 'Closed' : `${hours.saturday.open}-${hours.saturday.close}`,
        Sunday: hours.sunday.closed ? 'Closed' : `${hours.sunday.open}-${hours.sunday.close}`,
      };

      await updateRestaurant(selectedLocationId, { hours: hoursFormat });

      setOriginalHours(JSON.parse(JSON.stringify(hours)));
      setIsEditHoursMode(false);
      
      // Refresh restaurant data from DynamoDB
      if (refetch) {
        await refetch();
      }
    } catch (error) {
      console.error('Error saving hours:', error);
      alert(error.message || 'Failed to save hours. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // Handle save for Holiday Hours
  const handleSaveHolidayHours = async () => {
    if (!selectedLocationId) {
      alert('Please select a restaurant');
      return;
    }

    // Validate all holidays before saving
    const errors = [];
    const seenDates = new Set();
    
    holidayHours.forEach((holiday, index) => {
      // Check name
      if (!holiday.name || holiday.name.trim().length === 0) {
        errors.push(`Holiday ${index + 1}: Name is required`);
      } else if (holiday.name.length > 50) {
        errors.push(`Holiday ${index + 1}: Name must be 50 characters or less`);
      }
      
      // Check date format
      if (!holiday.date) {
        errors.push(`Holiday ${index + 1}: Date is required`);
      } else if (!isValidHolidayDate(holiday.date)) {
        errors.push(`Holiday ${index + 1}: Date must be in M/D format (e.g., 12/25)`);
      } else {
        // Check for duplicate dates
        if (seenDates.has(holiday.date)) {
          errors.push(`Holiday ${index + 1}: Duplicate date ${holiday.date}`);
        }
        seenDates.add(holiday.date);
      }
    });

    if (errors.length > 0) {
      alert('Please fix the following errors:\n\n' + errors.join('\n'));
      return;
    }

    setIsSaving(true);
    try {
      // Convert holiday hours to DynamoDB format
      const holidayHoursFormat = holidayHours.map(holiday => ({
        name: holiday.name.trim(),
        date: holiday.date,
        hours: holiday.closed ? 'CLOSED' : `${holiday.open}-${holiday.close}`
      }));

      await updateRestaurant(selectedLocationId, { holidayHours: holidayHoursFormat });

      setOriginalHolidayHours(JSON.parse(JSON.stringify(holidayHours)));
      setIsEditHolidayHoursMode(false);
      
      // Refresh restaurant data from DynamoDB
      if (refetch) {
        await refetch();
      }
    } catch (error) {
      console.error('Error saving holiday hours:', error);
      alert(error.message || 'Failed to save holiday hours. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // Handle hours change
  const handleHoursChange = (day, field, value) => {
    setHours(prev => ({
      ...prev,
      [day]: {
        ...prev[day],
        [field]: value
      }
    }));
  };

  // Toggle day closed
  const toggleDayClosed = (day) => {
    setHours(prev => ({
      ...prev,
      [day]: {
        ...prev[day],
        closed: !prev[day].closed
      }
    }));
  };

  // Format date from "M/D" to readable format (e.g., "December 25")
  const formatHolidayDate = (dateStr) => {
    if (!dateStr) return '';
    const [month, day] = dateStr.split('/').map(Number);
    if (!month || !day) return dateStr;
    
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    
    return `${months[month - 1]} ${day}`;
  };

  // Validate date format (M/D)
  const isValidHolidayDate = (dateStr) => {
    if (!dateStr) return false;
    
    // Check format M/D or MM/DD
    const dateRegex = /^(0?[1-9]|1[0-2])\/(0?[1-9]|[12][0-9]|3[01])$/;
    if (!dateRegex.test(dateStr)) return false;
    
    const [month, day] = dateStr.split('/').map(Number);
    
    // Validate month
    if (month < 1 || month > 12) return false;
    
    // Validate day based on month
    const daysInMonth = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
    if (day < 1 || day > daysInMonth[month - 1]) return false;
    
    return true;
  };

  // Sort holidays by date (month, then day)
  const getSortedHolidays = () => {
    return [...holidayHours].sort((a, b) => {
      const [aMonth, aDay] = a.date.split('/').map(Number);
      const [bMonth, bDay] = b.date.split('/').map(Number);
      
      if (aMonth !== bMonth) return aMonth - bMonth;
      return aDay - bDay;
    });
  };

  // Holiday Hours manipulation
  const addHoliday = () => {
    setHolidayHours(prev => [...prev, {
      id: `holiday-${Date.now()}`,
      name: '',
      date: '',
      open: '09:00',
      close: '21:00',
      closed: false
    }]);
  };

  const removeHoliday = (id) => {
    setHolidayHours(prev => prev.filter(h => h.id !== id));
  };

  const handleHolidayChange = (id, field, value) => {
    setHolidayHours(prev => prev.map(holiday => 
      holiday.id === id ? { ...holiday, [field]: value } : holiday
    ));
  };

  const toggleHolidayClosed = (id) => {
    setHolidayHours(prev => prev.map(holiday =>
      holiday.id === id ? { ...holiday, closed: !holiday.closed } : holiday
    ));
  };

  // Convert 24-hour time to 12-hour format
  const convertTo12Hour = (time24) => {
    if (!time24 || time24 === '—') return time24;
    const [hours, minutes] = time24.split(':');
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  const daysOfWeek = [
    { key: 'monday', label: 'Monday' },
    { key: 'tuesday', label: 'Tuesday' },
    { key: 'wednesday', label: 'Wednesday' },
    { key: 'thursday', label: 'Thursday' },
    { key: 'friday', label: 'Friday' },
    { key: 'saturday', label: 'Saturday' },
    { key: 'sunday', label: 'Sunday' },
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#3AD0E6]" />
      </div>
    );
  }

  if (!restaurants || restaurants.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-400">No restaurants available</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Location Selector */}
      <Card className="bg-gray-800 border-gray-700">
        <CardContent className="p-6 py-5">
          <div className="w-full">
            <label className="block text-sm font-medium text-gray-300 mb-3">
              Select Location
            </label>
            <Select value={selectedLocationId} onValueChange={setSelectedLocationId}>
              <SelectTrigger className="w-full h-11">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {restaurants.map((restaurant) => (
                  <SelectItem key={restaurant.locationId} value={restaurant.locationId}>
                    {restaurant.restaurantName || restaurant.name || `Location ${restaurant.locationId}`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-gray-400 mt-2">
              Select which location's information you want to view or edit
            </p>
          </div>
        </CardContent>
      </Card>

      {/* General Info Section */}
      <Card className="bg-gray-800 border-gray-700">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-white">General Info</h3>
              <p className="text-sm text-gray-400">Basic location information</p>
            </div>
            {!isEditMode && (
              <Button
                onClick={handleEditGeneralInfo}
                className="bg-[#3AD0E6] hover:bg-[#3AD0E6]/80 text-white"
                size="sm"
              >
                <Edit2 className="w-4 h-4 mr-2" />
                Edit
              </Button>
            )}
            {isEditMode && (
              <div className="flex gap-2">
                <Button
                  onClick={handleCancelGeneralInfo}
                  variant="outline"
                  className="border-gray-700 text-gray-300 hover:bg-gray-700"
                  size="sm"
                >
                  <X className="w-4 h-4 mr-2" />
                  Cancel
                </Button>
                    <Button
                      onClick={handleSaveGeneralInfo}
                      className="bg-[#3AD0E6] hover:bg-[#3AD0E6]/80 text-white"
                      size="sm"
                      disabled={isSaving}
                    >
                      <Save className="w-4 h-4 mr-2" />
                      {isSaving ? 'Saving...' : 'Save'}
                    </Button>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Location Name
            </label>
            {isEditMode ? (
              <Input
                type="text"
                value={restaurantName}
                onChange={(e) => setRestaurantName(e.target.value)}
                placeholder="Enter location name"
              />
            ) : (
              <p className="text-white py-2 px-3 bg-gray-900 rounded border border-gray-700">
                {restaurantName || '—'}
              </p>
            )}
          </div>

          <div>
            <label htmlFor="yapn-number" className="block text-sm font-medium text-gray-300 mb-2">
              Yapn Number
            </label>
            <Input
              id="yapn-number"
              type="tel"
              value={formatPhoneNumber(selectedRestaurant?.yapnNumber)}
              readOnly
              className="bg-gray-900"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Address
            </label>
            {isEditMode ? (
              <Input
                type="text"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder="123 Main St, City, State ZIP"
              />
            ) : (
              <p className="text-white py-2 px-3 bg-gray-900 rounded border border-gray-700">
                {address || '—'}
              </p>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Time Zone
              </label>
              {isEditMode ? (
                <Select value={timeZone} onValueChange={setTimeZone}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="America/New_York">Eastern Time (ET)</SelectItem>
                    <SelectItem value="America/Chicago">Central Time (CT)</SelectItem>
                    <SelectItem value="America/Denver">Mountain Time (MT)</SelectItem>
                    <SelectItem value="America/Los_Angeles">Pacific Time (PT)</SelectItem>
                    <SelectItem value="America/Phoenix">Arizona Time (MST)</SelectItem>
                    <SelectItem value="America/Anchorage">Alaska Time (AKT)</SelectItem>
                    <SelectItem value="Pacific/Honolulu">Hawaii Time (HST)</SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                <p className="text-white py-2 px-3 bg-gray-900 rounded border border-gray-700">
                  {timeZone.replace('America/', '').replace('Pacific/', '').replace('_', ' ') || '—'}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Primary Language
              </label>
              {isEditMode ? (
                <Select value={primaryLanguage} onValueChange={setPrimaryLanguage}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="en">English</SelectItem>
                    <SelectItem value="es">Spanish</SelectItem>
                    <SelectItem value="fr">French</SelectItem>
                    <SelectItem value="de">German</SelectItem>
                    <SelectItem value="it">Italian</SelectItem>
                    <SelectItem value="pt">Portuguese</SelectItem>
                    <SelectItem value="zh">Chinese</SelectItem>
                    <SelectItem value="ja">Japanese</SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                <p className="text-white py-2 px-3 bg-gray-900 rounded border border-gray-700">
                  {primaryLanguage === 'en' ? 'English' : 
                   primaryLanguage === 'es' ? 'Spanish' :
                   primaryLanguage === 'fr' ? 'French' :
                   primaryLanguage === 'de' ? 'German' :
                   primaryLanguage === 'it' ? 'Italian' :
                   primaryLanguage === 'pt' ? 'Portuguese' :
                   primaryLanguage === 'zh' ? 'Chinese' :
                   primaryLanguage === 'ja' ? 'Japanese' : primaryLanguage}
                </p>
              )}
            </div>
          </div>

          {/* Outbound Report Recipient - Only show if location has outbound enabled */}
          {selectedRestaurant?.outbound && (
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Outbound Report Recipient
              </label>
              {isEditMode ? (
                <Input
                  type="email"
                  value={outboundReportRecipient}
                  onChange={(e) => setOutboundReportRecipient(e.target.value)}
                  placeholder="email@example.com"
                />
              ) : (
                <p className="text-white py-2 px-3 bg-gray-900 rounded border border-gray-700">
                  {outboundReportRecipient || '—'}
                </p>
              )}
              <p className="text-xs text-gray-400 mt-1">
                Email address to receive outbound campaign completion reports
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      <TransferNumbersSection
        key={`transfer-${selectedLocationId}`}
        locationId={selectedRestaurant?.locationId}
        transferNumber={selectedRestaurant?.transferNumber}
        onSaved={refetch}
      />

      {selectedRestaurant && Object.prototype.hasOwnProperty.call(selectedRestaurant, 'leadsEmail') && (
        <LeadsEmailSection
          key={`leads-${selectedLocationId}`}
          locationId={selectedRestaurant.locationId}
          leadsEmail={selectedRestaurant.leadsEmail}
          onSaved={refetch}
        />
      )}

      {/* Hours of Operation Section */}
      <Card className="bg-gray-800 border-gray-700">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-white">Hours of Operation</h3>
              <p className="text-sm text-gray-400">Set your location's operating hours</p>
            </div>
            {!isEditHoursMode && (
              <Button
                onClick={handleEditHours}
                className="bg-[#3AD0E6] hover:bg-[#3AD0E6]/80 text-white"
                size="sm"
              >
                <Edit2 className="w-4 h-4 mr-2" />
                Edit
              </Button>
            )}
            {isEditHoursMode && (
              <div className="flex gap-2">
                <Button
                  onClick={handleCancelHours}
                  variant="outline"
                  className="border-gray-700 text-gray-300 hover:bg-gray-700"
                  size="sm"
                >
                  <X className="w-4 h-4 mr-2" />
                  Cancel
                </Button>
                    <Button
                      onClick={handleSaveHours}
                      className="bg-[#3AD0E6] hover:bg-[#3AD0E6]/80 text-white"
                      size="sm"
                      disabled={isSaving}
                    >
                      <Save className="w-4 h-4 mr-2" />
                      {isSaving ? 'Saving...' : 'Save'}
                    </Button>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-700">
                  <th className="text-left text-sm font-medium text-gray-300 py-3 px-4">Day</th>
                  <th className="text-left text-sm font-medium text-gray-300 py-3 px-4">Open</th>
                  <th className="text-left text-sm font-medium text-gray-300 py-3 px-4">Close</th>
                  <th className="text-left text-sm font-medium text-gray-300 py-3 px-4">Status</th>
                </tr>
              </thead>
              <tbody>
                {daysOfWeek.map((day) => (
                  <tr key={day.key} className="border-b border-gray-700/50">
                    <td className="py-3 px-4 text-sm text-gray-300">{day.label}</td>
                    <td className="py-3 px-4">
                      {isEditHoursMode ? (
                        <Input
                          type="time"
                          value={hours[day.key].open}
                          onChange={(e) => handleHoursChange(day.key, 'open', e.target.value)}
                          disabled={hours[day.key].closed}
                          className="w-32"
                        />
                      ) : (
                        <p className="text-white py-2 px-3 bg-gray-900 rounded border border-gray-700 w-36">
                          {hours[day.key].closed ? '—' : convertTo12Hour(hours[day.key].open)}
                        </p>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      {isEditHoursMode ? (
                        <Input
                          type="time"
                          value={hours[day.key].close}
                          onChange={(e) => handleHoursChange(day.key, 'close', e.target.value)}
                          disabled={hours[day.key].closed}
                          className="w-32"
                        />
                      ) : (
                        <p className="text-white py-2 px-3 bg-gray-900 rounded border border-gray-700 w-36">
                          {hours[day.key].closed ? '—' : convertTo12Hour(hours[day.key].close)}
                        </p>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      {isEditHoursMode ? (
                        <button
                          type="button"
                          onClick={() => toggleDayClosed(day.key)}
                          className={`text-xs px-3 py-1 rounded-full ${
                            hours[day.key].closed
                              ? 'bg-red-900/20 text-red-400 border border-red-700/50'
                              : 'bg-green-900/20 text-green-400 border border-green-700/50'
                          }`}
                        >
                          {hours[day.key].closed ? 'Closed' : 'Open'}
                        </button>
                      ) : (
                        <span className={`text-xs px-3 py-1 rounded-full ${
                          hours[day.key].closed
                            ? 'bg-red-900/20 text-red-400 border border-red-700/50'
                            : 'bg-green-900/20 text-green-400 border border-green-700/50'
                        }`}>
                          {hours[day.key].closed ? 'Closed' : 'Open'}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Holiday Hours Section */}
      <Card className="bg-gray-800 border-gray-700">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-white">Holiday Hours</h3>
              <p className="text-sm text-gray-400">Set special hours for holidays</p>
            </div>
            {!isEditHolidayHoursMode && (
              <Button
                onClick={handleEditHolidayHours}
                className="bg-[#3AD0E6] hover:bg-[#3AD0E6]/80 text-white"
                size="sm"
              >
                <Edit2 className="w-4 h-4 mr-2" />
                Edit
              </Button>
            )}
            {isEditHolidayHoursMode && (
              <div className="flex gap-2">
                <Button
                  onClick={handleCancelHolidayHours}
                  variant="outline"
                  className="border-gray-700 text-gray-300 hover:bg-gray-700"
                  size="sm"
                >
                  <X className="w-4 h-4 mr-2" />
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveHolidayHours}
                  className="bg-[#3AD0E6] hover:bg-[#3AD0E6]/80 text-white"
                  size="sm"
                  disabled={isSaving}
                >
                  <Save className="w-4 h-4 mr-2" />
                  {isSaving ? 'Saving...' : 'Save'}
                </Button>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {holidayHours.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-400 mb-4">No holiday hours configured</p>
                {isEditHolidayHoursMode && (
                  <Button
                    onClick={addHoliday}
                    variant="outline"
                    className="border-gray-700 text-gray-300 hover:bg-gray-700"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Holiday
                  </Button>
                )}
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-700">
                        <th className="text-left text-sm font-medium text-gray-300 py-3 px-4">Holiday Name</th>
                        <th className="text-left text-sm font-medium text-gray-300 py-3 px-4">Date (MM/DD)</th>
                        <th className="text-left text-sm font-medium text-gray-300 py-3 px-4">Open</th>
                        <th className="text-left text-sm font-medium text-gray-300 py-3 px-4">Close</th>
                        <th className="text-left text-sm font-medium text-gray-300 py-3 px-4">Status</th>
                        {isEditHolidayHoursMode && (
                          <th className="text-left text-sm font-medium text-gray-300 py-3 px-4">Action</th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {(isEditHolidayHoursMode ? holidayHours : getSortedHolidays()).map((holiday) => (
                        <tr key={holiday.id} className="border-b border-gray-700/50">
                          <td className="py-3 px-4">
                            {isEditHolidayHoursMode ? (
                              <Input
                                type="text"
                                value={holiday.name}
                                onChange={(e) => handleHolidayChange(holiday.id, 'name', e.target.value)}
                                placeholder="e.g., Christmas"
                                className="w-full"
                              />
                            ) : (
                              <p className="text-white text-sm">{holiday.name || '—'}</p>
                            )}
                          </td>
                          <td className="py-3 px-4">
                            {isEditHolidayHoursMode ? (
                              <div>
                                <Input
                                  type="text"
                                  value={holiday.date}
                                  onChange={(e) => handleHolidayChange(holiday.id, 'date', e.target.value)}
                                  placeholder="12/25"
                                  className="w-24"
                                />
                                <p className="text-xs text-gray-400 mt-1">Format: M/D</p>
                              </div>
                            ) : (
                              <p className="text-white text-sm">
                                {holiday.date ? formatHolidayDate(holiday.date) : '—'}
                              </p>
                            )}
                          </td>
                          <td className="py-3 px-4">
                            {isEditHolidayHoursMode ? (
                              <Input
                                type="time"
                                value={holiday.open}
                                onChange={(e) => handleHolidayChange(holiday.id, 'open', e.target.value)}
                                disabled={holiday.closed}
                                className="w-32"
                              />
                            ) : (
                              <p className="text-white py-2 px-3 bg-gray-900 rounded border border-gray-700 w-36">
                                {holiday.closed ? '—' : convertTo12Hour(holiday.open)}
                              </p>
                            )}
                          </td>
                          <td className="py-3 px-4">
                            {isEditHolidayHoursMode ? (
                              <Input
                                type="time"
                                value={holiday.close}
                                onChange={(e) => handleHolidayChange(holiday.id, 'close', e.target.value)}
                                disabled={holiday.closed}
                                className="w-32"
                              />
                            ) : (
                              <p className="text-white py-2 px-3 bg-gray-900 rounded border border-gray-700 w-36">
                                {holiday.closed ? '—' : convertTo12Hour(holiday.close)}
                              </p>
                            )}
                          </td>
                          <td className="py-3 px-4">
                            {isEditHolidayHoursMode ? (
                              <button
                                type="button"
                                onClick={() => toggleHolidayClosed(holiday.id)}
                                className={`text-xs px-3 py-1 rounded-full ${
                                  holiday.closed
                                    ? 'bg-red-900/20 text-red-400 border border-red-700/50'
                                    : 'bg-green-900/20 text-green-400 border border-green-700/50'
                                }`}
                              >
                                {holiday.closed ? 'Closed' : 'Open'}
                              </button>
                            ) : (
                              <span className={`text-xs px-3 py-1 rounded-full ${
                                holiday.closed
                                  ? 'bg-red-900/20 text-red-400 border border-red-700/50'
                                  : 'bg-green-900/20 text-green-400 border border-green-700/50'
                              }`}>
                                {holiday.closed ? 'Closed' : 'Open'}
                              </span>
                            )}
                          </td>
                          {isEditHolidayHoursMode && (
                            <td className="py-3 px-4">
                              <button
                                type="button"
                                onClick={() => removeHoliday(holiday.id)}
                                className="text-red-400 hover:text-red-300 transition-colors"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {isEditHolidayHoursMode && (
                  <Button
                    onClick={addHoliday}
                    variant="outline"
                    className="border-gray-700 text-gray-300 hover:bg-gray-700"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Holiday
                  </Button>
                )}
              </>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default OrganizationTab;
